import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Zap, TrendingUp, Package, Clock, CheckCircle, ArrowUpRight, ArrowDownRight, RefreshCw, Coins, Star, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BADGE_COLORS = {
  'Popüler': 'bg-green-100 text-green-700 border-green-200',
  'En İyi': 'bg-blue-100 text-blue-700 border-blue-200',
  '%25 Tasarruf': 'bg-amber-100 text-amber-700 border-amber-200',
};

export default function CreditsPage() {
  const { token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0, total_purchased: 0, total_spent: 0 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [payStatus, setPayStatus] = useState(null); // 'success' | 'cancelled' | null

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [pkgRes, balRes, txnRes] = await Promise.all([
        axios.get(`${API}/api/credits/packages`),
        axios.get(`${API}/api/credits/balance`, { headers }),
        axios.get(`${API}/api/credits/transactions`, { headers }),
      ]);
      setPackages(pkgRes.data.packages || []);
      setWallet(balRes.data);
      setTransactions(txnRes.data.transactions || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const cancelled = params.get('cancelled');

    if (cancelled) {
      setPayStatus('cancelled');
      return;
    }
    if (!sessionId) return;

    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/api/credits/status/${sessionId}`, { headers });
        if (res.data.payment_status === 'paid') {
          setPayStatus('success');
          setWallet(w => ({ ...w, balance: res.data.balance }));
          await fetchData();
          navigate(location.pathname, { replace: true });
        } else if (attempts < 8) {
          attempts++;
          setTimeout(poll, 2000);
        }
      } catch (e) {
        console.error(e);
      }
    };
    poll();
  }, [location.search]);

  const handleBuy = async (pkg) => {
    setPurchasing(pkg.id);
    try {
      const res = await axios.post(`${API}/api/credits/purchase`, {
        package_id: pkg.id,
        origin_url: window.location.origin,
      }, { headers });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Satın alma başlatılamadı');
      setPurchasing(null);
    }
  };

  const txnIcon = (txn) => {
    if (txn.type === 'purchase' || txn.amount > 0) return <ArrowUpRight className="w-4 h-4 text-green-500" />;
    return <ArrowDownRight className="w-4 h-4 text-red-400" />;
  };
  const txnColor = (txn) => txn.amount > 0 ? 'text-green-600 font-semibold' : 'text-red-500 font-semibold';

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-green-600" />
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Coins className="w-7 h-7 text-amber-500" />
          Kredi Cüzdanı
        </h1>
        <p className="text-gray-500 text-sm mt-1">Vitrin, öne çıkarma ve premium özellikler için kredi kullanın.</p>
      </div>

      {/* Status Banner */}
      {payStatus === 'success' && (
        <div data-testid="credit-purchase-success" className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4 text-green-700">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium">Krediler başarıyla eklendi! Yeni bakiyeniz: <strong>{wallet.balance} kredi</strong></span>
        </div>
      )}
      {payStatus === 'cancelled' && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-700">
          <Clock className="w-5 h-5 flex-shrink-0" />
          <span>Satın alma iptal edildi. Dilediğiniz zaman tekrar deneyebilirsiniz.</span>
        </div>
      )}

      {/* Wallet Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div data-testid="credit-balance" className="bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl p-5 text-white shadow-lg">
          <p className="text-amber-100 text-sm font-medium">Mevcut Bakiye</p>
          <p className="text-4xl font-black mt-1">{wallet.balance.toLocaleString()}</p>
          <p className="text-amber-100 text-xs mt-1">kredi</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <p className="text-gray-500 text-sm">Toplam Satın Alınan</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{wallet.total_purchased?.toLocaleString() || 0}</p>
          <p className="text-gray-400 text-xs mt-1 flex items-center gap-1"><ArrowUpRight className="w-3 h-3 text-green-500" />kredi</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <p className="text-gray-500 text-sm">Toplam Harcanan</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{wallet.total_spent?.toLocaleString() || 0}</p>
          <p className="text-gray-400 text-xs mt-1 flex items-center gap-1"><ArrowDownRight className="w-3 h-3 text-red-400" />kredi</p>
        </div>
      </div>

      {/* Credit Packages */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" />
          Kredi Paketi Satın Al
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {packages.map(pkg => (
            <div
              key={pkg.id}
              data-testid={`credit-pkg-${pkg.id}`}
              className={`relative bg-white border-2 rounded-2xl p-5 flex flex-col gap-3 transition-all hover:shadow-md ${
                pkg.is_popular ? 'border-green-400 shadow-green-50 shadow-md' : 'border-gray-100'
              }`}
            >
              {pkg.badge && (
                <span className={`absolute -top-2.5 left-4 text-xs font-bold px-2.5 py-0.5 rounded-full border ${BADGE_COLORS[pkg.badge] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                  {pkg.badge}
                </span>
              )}
              <div>
                <p className="text-sm text-gray-500 font-medium">{pkg.name}</p>
                <p className="text-3xl font-black text-gray-900 mt-0.5">{pkg.credits.toLocaleString()}</p>
                <p className="text-xs text-gray-400">kredi</p>
              </div>
              <div className="flex-1" />
              <div>
                <p className="text-2xl font-bold text-gray-900">${pkg.price_usd}</p>
                <p className="text-xs text-gray-400">${(pkg.price_usd / pkg.credits * 100).toFixed(1)} / 100 kredi</p>
                {pkg.savings_pct > 0 && (
                  <p className="text-xs text-green-600 font-medium mt-0.5">%{pkg.savings_pct} tasarruf</p>
                )}
              </div>
              <button
                data-testid={`buy-credits-${pkg.id}`}
                onClick={() => handleBuy(pkg)}
                disabled={!!purchasing}
                className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all ${
                  pkg.is_popular
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-900 hover:bg-gray-800 text-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {purchasing === pkg.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />Yönlendiriliyor...
                  </span>
                ) : (
                  'Satın Al'
                )}
              </button>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-3">
          100 kredi = $10 | Vitrine proje ekleme: <strong>100 kredi / 30 gün</strong>
        </p>
      </div>

      {/* Transaction History */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-500" />
          Kredi Cari Hesabı
        </h2>
        {transactions.length === 0 ? (
          <div className="text-center py-12 bg-white border border-dashed border-gray-200 rounded-2xl text-gray-400">
            <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="font-medium">Henüz işlem yok</p>
            <p className="text-sm">Yukarıdan kredi satın aldığınızda burada görünecek</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Tarih</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Açıklama</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Kredi</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Bakiye</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {transactions.map(txn => (
                  <tr key={txn.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3.5 text-gray-500 whitespace-nowrap">
                      {new Date(txn.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </td>
                    <td className="px-5 py-3.5 text-gray-700 flex items-center gap-2">
                      {txnIcon(txn)}
                      {txn.description}
                    </td>
                    <td className={`px-5 py-3.5 text-right ${txnColor(txn)}`}>
                      {txn.amount > 0 ? `+${txn.amount}` : txn.amount}
                    </td>
                    <td className="px-5 py-3.5 text-right text-gray-700 font-medium">
                      {txn.balance_after?.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
